import React from "react";

function OpportunityDetails({ opportunity, onClose, onEdit, onUpdateStatus, onDelete }) {
  if (!opportunity) return null;

  // Mapping pipeline stages to progress percentages
  const stageProgress = {
    New: 0,
    "Proposal Sent": 25,
    Negotiation: 50,
    "Closed - Won": 100,
  };

  const handleMarkWon = () => onUpdateStatus(opportunity.id, "Closed - Won");
  const handleMarkLost = () => onUpdateStatus(opportunity.id, "Closed - Lost");

  return (
    <div className="fixed top-0 right-0 w-full md:w-1/3 h-full bg-white shadow-lg z-50 flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b bg-gray-100">
        <h2 className="text-xl font-bold text-gray-800">Opportunity Details</h2>
        <button className="text-gray-600 hover:text-gray-900 text-2xl" onClick={onClose}>
          &times;
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Opportunity Information */}
        <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
          <h3 className="text-lg font-bold text-gray-800 mb-3 border-b pb-2">
            Opportunity Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <p>
              <span className="text-sm font-semibold text-gray-600">Name:</span>{" "}
              <span className="text-gray-800">{opportunity.opportunity_name}</span>
            </p>
            <p>
              <span className="text-sm font-semibold text-gray-600">Product/Service:</span>{" "}
              <span className="text-gray-800">{opportunity.product_service}</span>
            </p>
            <p>
              <span className="text-sm font-semibold text-gray-600">Pipeline Stage:</span>{" "}
              <span className="text-gray-800">{opportunity.PipelineStage?.name || "N/A"}</span>
            </p>
            <p>
              <span className="text-sm font-semibold text-gray-600">Status:</span>{" "}
              <span className="text-gray-800">{opportunity.OpportunityStatus?.name || "N/A"}</span>
            </p>
            <p>
              <span className="text-sm font-semibold text-gray-600">Estimated Value:</span>{" "}
              <span className="text-gray-800">
                ${opportunity.estimated_value?.toLocaleString() || "0"}
              </span>
            </p>
            <p>
              <span className="text-sm font-semibold text-gray-600">Probability:</span>{" "}
              <span className="text-gray-800">{opportunity.probability || "0"}%</span>
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-gray-600 mb-1">Progress:</h4>
            <div className="flex items-center">
              <div className="w-full bg-gray-200 rounded h-4">
                <div
                  className="bg-blue-600 h-4 rounded"
                  style={{
                    width: `${stageProgress[opportunity.PipelineStage?.name] || 0}%`,
                  }}
                ></div>
              </div>
              <span className="ml-2 text-sm text-gray-700">
                {stageProgress[opportunity.PipelineStage?.name] || 0}%
              </span>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="text-lg font-bold text-gray-800 mb-3 border-b pb-2">
            Additional Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <p>
              <span className="text-sm font-semibold text-gray-600">Reason for Loss:</span>{" "}
              <span className="text-gray-800">{opportunity.reason_for_loss || "N/A"}</span>
            </p>
            <p>
              <span className="text-sm font-semibold text-gray-600">Assigned To:</span>{" "}
              <span className="text-gray-800">
                {opportunity.User
                  ? `${opportunity.User.first_name} ${opportunity.User.last_name}`
                  : "N/A"}
              </span>
            </p>
            <p>
              <span className="text-sm font-semibold text-gray-600">Lead ID:</span>{" "}
              <span className="text-gray-800">{opportunity.lead_id || "N/A"}</span>
            </p>
            <p>
              <span className="text-sm font-semibold text-gray-600">Customer:</span>{" "}
              <span className="text-gray-800">
                {opportunity.Customer?.company_name || opportunity.Customer?.name || "N/A"}
              </span>
            </p>
          </div>
        </div>

        {/* Timestamps */}
        <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
          <h3 className="text-lg font-bold text-gray-800 mb-3 border-b pb-2">
            Timestamps
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <p>
              <span className="text-sm font-semibold text-gray-600">Expected Close Date:</span>{" "}
              <span className="text-gray-800">{opportunity.expected_close_date || "N/A"}</span>
            </p>
            <p>
              <span className="text-sm font-semibold text-gray-600">Created At:</span>{" "}
              <span className="text-gray-800">{opportunity.created_date || "N/A"}</span>
            </p>
            <p>
              <span className="text-sm font-semibold text-gray-600">Last Updated:</span>{" "}
              <span className="text-gray-800">{opportunity.last_updated || "N/A"}</span>
            </p>
          </div>
        </div>
      </div>

      {/* ✅ Sticky Footer with Quick Actions */}
      <div className="p-4 border-t bg-gray-100 flex justify-between items-center">
        {/* Quick Actions */}
        <div className="flex space-x-2">
          <button
            className={`bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-md text-sm font-medium ${
              opportunity.Status?.name === "Closed - Won" ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={handleMarkWon}
            disabled={opportunity.Status?.name === "Closed - Won"}
          >
            Mark as Won
          </button>
          <button
            className={`bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-md text-sm font-medium ${
              opportunity.Status?.name === "Closed - Lost" ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={handleMarkLost}
            disabled={opportunity.Status?.name === "Closed - Lost"}
          >
            Mark as Lost
          </button>
        </div>

        {/* Edit and Delete */}
        <div className="flex space-x-2">
          <button className="bg-yellow-500 hover:bg-yellow-400 text-white px-4 py-2 rounded-md text-sm font-medium" onClick={() => onEdit(opportunity)}>
            Edit
          </button>
          <button className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-md text-sm font-medium" onClick={() => onDelete(opportunity.id)}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default OpportunityDetails;